/**
 * Admin sign-in (/admin-login) logic for AdminLogin.jsx (both looks since v1.71): Firebase
 * email/password sign-in, then a live read of users/{uid}.userType; admins go
 * to /admin, everyone else sees "Restricted. Admins only."
 */
import { useState } from 'react';
import { Platform } from 'react-native';
import { doc, onSnapshot } from 'firebase/firestore';
import { signInWithEmailHelper, db } from '../firebaseConfig';

export const ADMIN_RESTRICTED = 'Restricted. Admins only.';

/** Firebase auth error code → the message shown under the form. */
export function adminSignInMessage(code = '') {
  if (code.includes('invalid-email')) return 'Invalid email format';
  if (code.includes('user-not-found')) return 'No account found for this email';
  if (code.includes('wrong-password') || code.includes('invalid-credential')) return 'Incorrect email or password';
  if (code.includes('too-many-requests')) return 'Too many attempts. Try again later';
  if (code.includes('network-request-failed')) return 'Network error. Check your connection';
  return 'Sign-in failed';
}

const go = (path) => {
  try {
    if (Platform.OS === 'web') {
      window.history.pushState({}, '', path);
      window.dispatchEvent(new Event('app:navigate'));
    }
  } catch {}
};

export default function useAdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [showRestricted, setShowRestricted] = useState(false);

  const goHome = () => go('/');

  const onSignIn = async () => {
    if (!email || !password) {
      setStatus('Enter email and password');
      return;
    }
    try {
      setLoading(true);
      setStatus('Signing in...');
      const user = await signInWithEmailHelper(email.trim(), password);
      setStatus('Checking permissions...');
      let done = false;
      const ref = doc(db, 'users', user.uid);
      // Real-time check to avoid stale reads right after sign-in
      const unsub = onSnapshot(ref, (snap) => {
        if (done) return;
        const isAdmin = !!(snap.exists() && snap.data().userType === 'admin');
        done = true;
        unsub();
        if (isAdmin) {
          setStatus('');
          go('/admin');
        } else {
          setShowRestricted(true);
          setStatus(ADMIN_RESTRICTED);
        }
      }, () => {
        setShowRestricted(true);
        setStatus(ADMIN_RESTRICTED);
      });
    } catch (e) {
      setStatus(adminSignInMessage(e?.code || ''));
    } finally {
      setLoading(false);
    }
  };

  return { email, setEmail, password, setPassword, status, loading, showRestricted, onSignIn, goHome };
}
